class KtSocialProofVideos extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = 'true';
    this.viewport = this.querySelector('[data-kt-social-viewport]');
    this.mobileQuery = window.matchMedia('(max-width: 749px), (pointer: coarse)');
    this.cards = [...this.querySelectorAll('[data-kt-social-card]')];
    this.providers = [...this.querySelectorAll('[data-kt-external-video]')];
    this.querySelector('[data-kt-social-prev]')?.addEventListener('click', () => this.move(-1));
    this.querySelector('[data-kt-social-next]')?.addEventListener('click', () => this.move(1));
    this.querySelectorAll('[data-kt-social-focus]').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.stopPropagation();
        this.toggleFocus(button.closest('[data-kt-social-card]'), button);
      });
    });
    this.querySelectorAll('[data-kt-social-fullscreen]').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.stopPropagation();
        this.openFullscreen(button.closest('[data-kt-social-card]'), button);
      });
    });
    this.viewport?.addEventListener('keydown', (event) => {
      if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      event.preventDefault();
      this.move(event.key === 'ArrowRight' ? 1 : -1);
    });
    this.cards.forEach((card, index) => {
      card.addEventListener('click', (event) => {
        if (event.target.closest('a, button, video')) return;
        this.center(index);
      });
      card.addEventListener('keydown', (event) => {
        if (event.target !== card || !['Enter', ' '].includes(event.key)) return;
        event.preventDefault();
        this.center(index);
      });
      card.querySelector('video')?.addEventListener('play', () => this.setFocus(card, true));
    });
    this.fullscreenChangeHandler = () => this.updateFullscreenState();
    document.addEventListener('fullscreenchange', this.fullscreenChangeHandler);
    this.viewport?.addEventListener('scroll', () => {
      if (this.scrollFrame) return;
      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = null;
        this.updateActiveFromPosition();
      });
    }, { passive: true });
    this.providerObserver = new IntersectionObserver((entries) => {
      entries.filter((entry) => entry.isIntersecting).forEach((entry) => {
        this.loadProvider(entry.target);
        this.providerObserver.unobserve(entry.target);
      });
    }, { root: this.viewport, rootMargin: '0px 55%', threshold: 0.01 });
    this.providers.forEach((provider) => this.providerObserver.observe(provider));
    this.setActive(0);
    this.resizeObserver = new ResizeObserver(() => this.updateGeometry());
    if (this.viewport) this.resizeObserver.observe(this.viewport);
    requestAnimationFrame(() => {
      this.updateGeometry();
      this.center(0, 'auto');
    });
  }

  disconnectedCallback() {
    this.providerObserver?.disconnect();
    this.resizeObserver?.disconnect();
    if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
    document.removeEventListener('fullscreenchange', this.fullscreenChangeHandler);
  }

  move(direction) {
    const current = Math.max(0, this.cards.findIndex((card) => card.classList.contains('is-active')));
    const next = Math.min(this.cards.length - 1, Math.max(0, current + direction));
    this.center(next);
  }

  center(index, requestedBehavior) {
    const card = this.cards[index];
    if (!card || !this.viewport) return;
    const behavior = requestedBehavior || (matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth');
    const left = card.offsetLeft - ((this.viewport.clientWidth - card.offsetWidth) / 2);
    this.viewport.scrollTo({ left, behavior });
    this.setActive(index);
  }

  updateGeometry() {
    const firstCard = this.cards[0];
    if (!this.viewport || !firstCard) return;
    const sideSpace = Math.max(0, (this.viewport.clientWidth - firstCard.offsetWidth) / 2);
    this.style.setProperty('--kt-social-side-space', `${sideSpace}px`);
  }

  updateActiveFromPosition() {
    if (!this.viewport || !this.cards.length) return;
    const viewportCenter = this.viewport.scrollLeft + (this.viewport.clientWidth / 2);
    const distances = this.cards.map((card) => Math.abs((card.offsetLeft + (card.offsetWidth / 2)) - viewportCenter));
    this.setActive(distances.indexOf(Math.min(...distances)));
  }

  setActive(index) {
    this.cards.forEach((card, cardIndex) => {
      const active = cardIndex === index;
      card.classList.toggle('is-active', active);
      if (active) card.setAttribute('aria-current', 'true');
      else card.removeAttribute('aria-current');
    });
  }

  toggleFocus(card, button) {
    if (!card) return;
    this.setFocus(card, !card.classList.contains('is-focus'));
    button?.focus({ preventScroll: true });
  }

  setFocus(card, focused) {
    this.cards.forEach((item) => {
      const isTarget = item === card && focused;
      item.classList.toggle('is-focus', isTarget);
      const control = item.querySelector('[data-kt-social-focus]');
      control?.setAttribute('aria-pressed', String(isTarget));
      if (control) control.textContent = isTarget ? control.dataset.labelExit : control.dataset.labelFocus;
    });
    const index = this.cards.indexOf(card);
    this.setActive(index);
    requestAnimationFrame(() => {
      this.updateGeometry();
      this.center(index, focused ? undefined : 'auto');
    });
  }

  async openFullscreen(card, button) {
    if (!card) return;
    if (this.mobileQuery.matches) {
      this.setFocus(card, true);
      return;
    }
    this.fullscreenOpener = button;
    this.setFocus(card, true);
    if (!card.requestFullscreen) return;
    try {
      await card.requestFullscreen();
    } catch (_) {
      this.setFocus(card, true);
    }
  }

  updateFullscreenState() {
    this.cards.forEach((card) => card.classList.toggle('is-fullscreen', document.fullscreenElement === card));
    if (document.fullscreenElement || !this.fullscreenOpener) return;
    this.fullscreenOpener.focus({ preventScroll: true });
    this.fullscreenOpener = null;
  }

  loadProvider(provider) {
    if (provider.dataset.loaded) return;
    provider.dataset.loaded = 'true';
    if (provider.dataset.source === 'instagram') this.loadInstagram(provider);
    if (provider.dataset.source === 'google_drive') this.loadGoogleDrive(provider);
  }

  instagramUrl(rawUrl) {
    try {
      const url = new URL(rawUrl);
      if (!/(^|\.)instagram\.com$/i.test(url.hostname)) return null;
      const match = url.pathname.match(/^\/(reel|p)\/([A-Za-z0-9_-]+)/i);
      if (!match) return null;
      return `https://www.instagram.com/${match[1].toLowerCase()}/${match[2]}/`;
    } catch (_) { return null; }
  }

  loadInstagram(provider) {
    const canonicalUrl = this.instagramUrl(provider.dataset.url);
    if (!canonicalUrl) return;
    const embed = document.createElement('blockquote');
    embed.className = 'instagram-media';
    embed.dataset.instgrmPermalink = canonicalUrl;
    embed.dataset.instgrmVersion = '14';
    provider.prepend(embed);
    let mutationObserver;
    const markReady = () => {
      if (!provider.querySelector('iframe')) return;
      provider.classList.add('is-ready');
      mutationObserver?.disconnect();
    };
    mutationObserver = new MutationObserver(markReady);
    mutationObserver.observe(provider, { childList: true, subtree: true });
    KtSocialProofVideos.loadInstagramScript().then(() => {
      window.instgrm?.Embeds?.process();
      setTimeout(markReady, 400);
    }).catch(() => mutationObserver.disconnect());
  }

  static loadInstagramScript() {
    if (window.instgrm?.Embeds) return Promise.resolve();
    if (window.KTInstagramEmbedPromise) return window.KTInstagramEmbedPromise;
    window.KTInstagramEmbedPromise = new Promise((resolve, reject) => {
      const existing = document.querySelector('script[src="https://www.instagram.com/embed.js"]');
      if (existing) {
        existing.addEventListener('load', resolve, { once: true });
        existing.addEventListener('error', reject, { once: true });
        return;
      }
      const script = document.createElement('script');
      script.async = true;
      script.src = 'https://www.instagram.com/embed.js';
      script.onload = resolve;
      script.onerror = reject;
      document.head.append(script);
    });
    return window.KTInstagramEmbedPromise;
  }

  googleDriveUrl(rawUrl) {
    try {
      const url = new URL(rawUrl);
      if (!/(^|\.)drive\.google\.com$/i.test(url.hostname)) return null;
      const pathMatch = url.pathname.match(/\/file\/d\/([^/]+)/i);
      const fileId = pathMatch?.[1] || url.searchParams.get('id');
      return fileId ? `https://drive.google.com/file/d/${encodeURIComponent(fileId)}/preview` : null;
    } catch (_) { return null; }
  }

  loadGoogleDrive(provider) {
    const previewUrl = this.googleDriveUrl(provider.dataset.url);
    if (!previewUrl) return;
    const iframe = document.createElement('iframe');
    iframe.src = previewUrl;
    iframe.title = provider.dataset.title || 'Vidéo Google Drive';
    iframe.loading = 'lazy';
    iframe.allow = 'autoplay; encrypted-media';
    iframe.allowFullscreen = true;
    iframe.addEventListener('load', () => provider.classList.add('is-ready'), { once: true });
    provider.prepend(iframe);
  }
}

if (!customElements.get('kt-social-proof-videos')) customElements.define('kt-social-proof-videos', KtSocialProofVideos);
